import { useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Upload } from "antd";
import { useTranslation } from "react-i18next";

import api from "../../../api";
import { showErrorMessage, showSuccessMessage } from "../../../utils";

/**
 * "Upload Receipt" button for the Receipt Overview (SMT-997) — moved here from
 * the Alle Mandanten receipts tab. Reuses `api.kontax.uploadReceipt`.
 *
 * antd fires `beforeUpload` once per file but always with the full batch as the
 * second arg, so we upload the whole batch when the last file arrives and
 * refetch once via `onUploaded`.
 */
const UploadReceiptButton = ({
  email,
  onUploaded,
}: {
  email?: string;
  onUploaded: () => void;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  const [uploading, setUploading] = useState(false);

  const uploadBatch = async (files: File[]) => {
    if (!email) return;
    setUploading(true);
    let anySuccess = false;
    for (const file of files) {
      try {
        await api.kontax.uploadReceipt(email, file);
        anySuccess = true;
        showSuccessMessage(t("upload.success", { name: file.name }));
      } catch {
        showErrorMessage(t("upload.error", { name: file.name }));
      }
    }
    setUploading(false);
    if (anySuccess) onUploaded();
  };

  return (
    <Upload
      multiple
      showUploadList={false}
      beforeUpload={(file, fileList) => {
        // Upload the whole batch once, when the last file's beforeUpload fires.
        if (file === fileList[fileList.length - 1]) {
          void uploadBatch(fileList as File[]);
        }
        return Upload.LIST_IGNORE;
      }}
    >
      <Button
        icon={<UploadOutlined />}
        loading={uploading}
        disabled={!email}
        data-test="uploadReceiptButton"
      >
        {t("actions.uploadReceipt")}
      </Button>
    </Upload>
  );
};

export default UploadReceiptButton;
